import styled from '@emotion/styled'
import { Colors, Shapes } from 'src/components/theme'
import { PropertiesCompany, PropertiesOwnerResponse, PropertiesReportedOwner } from 'src/common-services'
import { PropertyRecordAccordion } from 'src/components/insights/property-records/property-record-accordion'
import { UnavailableCardContent } from 'src/components/insights/unavailable-card-content'
import { Chip, Grid, Link, Typography } from '@mui/material'
import { Email } from '@emotion-icons/material/Email'
import { Phone } from '@emotion-icons/material/Phone'
import { Public } from '@emotion-icons/material/Public'
import { Flex, FlexColumn } from 'src/components/flex-containers'
import { GridPairKey, GridPairValue } from 'src/components/grid-pair'
import { PhoneNumber } from 'src/components/insights/property-records/phone-number'
import { useMemo } from 'react'
import { formatString } from 'src/utils/format-string'

const Wrapper = styled(FlexColumn)`
  gap: ${Shapes.spacing[6]}px;
  padding-inline: ${Shapes.spacing[4]}px;
`
const SectionContent = styled(Flex)`
  gap: ${Shapes.spacing[4]}px;
`
const SectionColumn = styled(FlexColumn)`
  padding-right: ${Shapes.spacing[4]}px;
  border-right: 1px solid ${Colors.border};
  .MuiGrid-item {
    padding-bottom: ${Shapes.spacing[4]}px;
  }
  &:last-of-type {
    border-right: none;
  }
`
const StyledChip = styled(Chip)`
  margin: 0 8px;
`

const ReportedOwner = (props: { reportedOwners: PropertiesReportedOwner[] }) => {
  const { reportedOwners } = props
  const getName = (owner: PropertiesReportedOwner) => {
    if (owner.name) {
      return formatString(owner.name, 'name')
    }
    if (owner.firstName || owner.lastName) {
      return formatString(`${owner.firstName ?? ''} ${owner.lastName ?? ''}`, 'name')
    }
    return '-'
  }

  return (
    <FlexColumn gap={Shapes.spacing[2]}>
      <Typography variant="title2" color={Colors.textDark}>
        Reported Owner{reportedOwners.length > 1 ? 's' : ''}
      </Typography>
      {reportedOwners.map((reportedOwner, index) => {
        return (
          <PropertyRecordAccordion
            key={index + 'reportedOwner'}
            title={
              <Typography variant="textmd" color={Colors.textBlack}>
                {getName(reportedOwner)}
              </Typography>
            }
            content={
              <SectionContent>
                <SectionColumn
                  style={{
                    flex: 1,
                  }}>
                  <Grid container>
                    <GridPairKey>Type</GridPairKey>
                    <GridPairValue>{reportedOwner.type || '-'}</GridPairValue>
                  </Grid>
                </SectionColumn>
                <SectionColumn
                  style={{
                    flex: 2,
                  }}>
                  <Grid container>
                    <GridPairKey>Address</GridPairKey>
                    <GridPairValue>
                      {formatString(reportedOwner.mailingAddress || reportedOwner.address, 'address')}
                    </GridPairValue>
                  </Grid>
                </SectionColumn>
              </SectionContent>
            }
          />
        )
      })}
    </FlexColumn>
  )
}

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${Shapes.spacing[1]}px;
  color: ${Colors.blueDark};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`
type ArrayValuesRendererProps = {
  values: string[] | undefined
  type: 'phone' | 'text' | 'link' | 'email'
}
const ArrayValuesRenderer = (props: ArrayValuesRendererProps) => {
  const { values, type } = props

  const render = useMemo(() => {
    if (!values) {
      return <>-</>
    }
    switch (type) {
      case 'phone':
        return values.map(
          (value, index) =>
            value && (
              <div>
                <StyledLink href={`tel:${value}`} key={value + index}>
                  <Phone size={14} /> <PhoneNumber>{value}</PhoneNumber>
                </StyledLink>
              </div>
            )
        )
      case 'email':
        return values.map(
          (value, index) =>
            value && (
              <div>
                <StyledLink href={`mailto:${value}`} key={value + index}>
                  <Email size={14} /> {value}
                </StyledLink>
              </div>
            )
        )
      case 'link':
        return values.map(
          (value, index) =>
            value && (
              <div>
                <StyledLink href={`https://${value}`} key={value + index} target="_blank">
                  <Public size={14} /> {value}
                </StyledLink>
              </div>
            )
        )
      case 'text':
      default:
        return values.map((value, index) => value && <div key={value + index}>{value}</div>)
    }
  }, [values])
  return <FlexColumn gap={Shapes.spacing[1]}>{render}</FlexColumn>
}

const Companies = (props: { companies: PropertiesCompany[] }) => {
  const { companies } = props

  if (!companies.length) {
    return null
  }

  const getAddress = (addresses: string[] | undefined): string => {
    if (!addresses) {
      return '-'
    }
    const findAddress = addresses.find((address) => !!address)
    return formatString(findAddress, 'address')
  }

  return (
    <FlexColumn gap={Shapes.spacing[2]}>
      <Typography variant="title2" color={Colors.textDark}>
        Other Possible Owners
      </Typography>
      {companies.map((company, index) => {
        return (
          <PropertyRecordAccordion
            key={index + 'company'}
            title={
              <Typography variant="textmd" color={Colors.textBlack}>
                {formatString(company.name, 'name')}
                {company.isHighConfidence && <StyledChip label="High Confidence" variant="outlined" size="small" />}
              </Typography>
            }
            content={
              <SectionContent>
                <SectionColumn
                  style={{
                    flex: 1,
                  }}>
                  <Grid container>
                    <GridPairKey>Addresses</GridPairKey>
                    <GridPairValue>{getAddress(company.address)}</GridPairValue>
                    <GridPairKey>Phones</GridPairKey>
                    <GridPairValue>
                      <ArrayValuesRenderer values={company.phone} type="phone" />
                    </GridPairValue>
                    <GridPairKey>Industries</GridPairKey>
                    <GridPairValue>
                      <ArrayValuesRenderer values={company.industry} type="text" />
                    </GridPairValue>
                  </Grid>
                </SectionColumn>
                <SectionColumn
                  style={{
                    flex: 1,
                  }}>
                  <Grid container>
                    <GridPairKey>Emails</GridPairKey>
                    <GridPairValue>
                      <ArrayValuesRenderer values={company.email} type="email" />
                    </GridPairValue>
                    <GridPairKey>Websites</GridPairKey>
                    <GridPairValue>
                      <ArrayValuesRenderer values={company.website} type="link" />
                    </GridPairValue>
                  </Grid>
                </SectionColumn>
              </SectionContent>
            }
          />
        )
      })}
    </FlexColumn>
  )
}

export const PropertyRecordsDetailsOwners = (props: { owners: PropertiesOwnerResponse }) => {
  const { owners } = props
  if (!owners || (!owners.reportedOwners.length && !owners.companies.length)) {
    return <UnavailableCardContent message="Sorry, but we couldn't find the information requested for this property." />
  }

  return (
    <Wrapper>
      <ReportedOwner reportedOwners={owners.reportedOwners} />
      <Companies companies={owners.companies} />
    </Wrapper>
  )
}
